import type { AccountAddress, ChainReference } from "./types";
import { CAIPValidationError } from "./errors";
import { Parser } from "./parser";

export type VmId = "evm" | "svm" | "sui";

export type EvmChainRef = {
  readonly vm: "evm";
  readonly chainNamespace: "eip155";
  readonly chainReference: ChainReference;
};

export type SvmChainRef = {
  readonly vm: "svm";
  readonly chainNamespace: "solana";
  readonly chainReference: ChainReference;
};

export type SuiChainRef = {
  readonly vm: "sui";
  readonly chainNamespace: "sui";
  readonly chainReference: ChainReference;
};

export type VmChainRef = EvmChainRef | SvmChainRef | SuiChainRef;
type VmChainRefForNamespace<TNamespace extends VmChainRef["chainNamespace"]> =
  Extract<VmChainRef, { readonly chainNamespace: TNamespace }>;

export type WalletIdentity =
  | {
      readonly vm: "evm";
      readonly address: AccountAddress;
    }
  | {
      readonly vm: "svm";
      readonly address: AccountAddress;
    }
  | {
      readonly vm: "sui";
      readonly address: AccountAddress;
    };

export type TransferReference =
  | {
      readonly kind: "evm_tx_hash";
      readonly value: `0x${string}`;
    }
  | {
      readonly kind: "svm_signature";
      readonly value: string;
    }
  | {
      readonly kind: "sui_digest";
      readonly digest: string;
    };

export type DepositDestinationAccount = Exclude<
  VmChainRef,
  { readonly vm: "sui" }
> & {
  readonly accountAddress: AccountAddress;
  readonly accountKind:
    | "evm_vault"
    | "solana_owner"
    | "solana_token_account"
    | "solana_ata"
    | "solana_pda_vault";
  readonly assetId?: string;
  readonly label?: string;
};

export type DepositAssetCandidate = VmChainRef & {
  readonly assetId: string;
  readonly address: string;
  readonly decimals: number;
  readonly symbol: string;
  readonly priceUsd?: number;
};

const EVM_ADDRESS_REGEX = /^0x[a-fA-F0-9]{40}$/u;
const SOLANA_BASE58_REGEX = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/u;
export const SUI_ADDRESS_REGEX = /^0x[0-9a-f]{64}$/u;
export const SUI_DIGEST_REGEX = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/u;
const BASE58_ALPHABET =
  "123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz";

export function chainRefKey(chain: VmChainRef): string {
  return `${chain.vm}:${chain.chainNamespace}:${chain.chainReference}`;
}

export function walletIdentityKey(wallet: WalletIdentity): string {
  return `${wallet.vm}:${wallet.address}`;
}

/** Parse a CAIP-2 network without guessing aliases or enabling a runtime. */
export function chainRefFromChainId(chainId: string): VmChainRef {
  const { namespace, reference } = Parser.parseChainId(chainId).chain;
  if (namespace !== "eip155" && namespace !== "solana" && namespace !== "sui") {
    throw new CAIPValidationError("Unsupported VM chain namespace", namespace);
  }
  return chainRefFromNamespace({
    chainNamespace: namespace,
    chainReference: reference,
  });
}

export function chainRefFromNamespace<
  TNamespace extends VmChainRef["chainNamespace"],
>(input: {
  readonly chainNamespace: TNamespace;
  readonly chainReference: ChainReference;
}): VmChainRefForNamespace<TNamespace>;
export function chainRefFromNamespace(input: {
  readonly chainNamespace: VmChainRef["chainNamespace"];
  readonly chainReference: ChainReference;
}): VmChainRef {
  switch (input.chainNamespace) {
    case "eip155":
      return {
        vm: "evm",
        chainNamespace: "eip155",
        chainReference: input.chainReference,
      };
    case "solana":
      return {
        vm: "svm",
        chainNamespace: "solana",
        chainReference: input.chainReference,
      };
    case "sui":
      return {
        vm: "sui",
        chainNamespace: "sui",
        chainReference: input.chainReference,
      };
    default:
      throw new CAIPValidationError(
        `Unsupported VM chain namespace: ${String(input.chainNamespace)}`,
        String(input.chainNamespace),
      );
  }
}

export function normalizeEvmAddress(
  address: string | null | undefined,
): AccountAddress | null {
  const trimmed = address?.trim();
  if (!trimmed || !EVM_ADDRESS_REGEX.test(trimmed)) {
    return null;
  }

  return trimmed.toLowerCase() as AccountAddress;
}

export function normalizeSvmAddress(
  address: string | null | undefined,
): AccountAddress | null {
  const trimmed = address?.trim();
  if (
    !trimmed ||
    !SOLANA_BASE58_REGEX.test(trimmed) ||
    !isBase58ByteWidth(trimmed, 32)
  ) {
    return null;
  }

  return trimmed as AccountAddress;
}

export function normalizeSuiAddress(
  address: string | null | undefined,
): AccountAddress | null {
  const normalized = address?.trim().toLowerCase();
  if (!normalized || !SUI_ADDRESS_REGEX.test(normalized)) {
    return null;
  }

  return normalized as AccountAddress;
}

/**
 * Validates the base58 encoding and decoded 32-byte width of a Sui digest.
 *
 * A character/length regex alone is insufficient: base58 strings of the same
 * character length can decode to different byte widths.
 */
export function isValidSuiDigest(value: string): boolean {
  if (!SUI_DIGEST_REGEX.test(value)) {
    return false;
  }

  return isBase58ByteWidth(value, 32);
}

/**
 * Decodes a Bitcoin-alphabet base58 string, or returns null when a character
 * is outside the alphabet. Solana keys, signatures and Sui digests are
 * case-sensitive, so callers compare the decoded bytes (or the verbatim
 * string), never a folded spelling. Dependency-free on purpose: nothing that
 * validates a provider response or an address should pull a chain SDK in.
 */
export function decodeBase58(value: string): Uint8Array | null {
  let leadingZeroBytes = 0;
  while (value[leadingZeroBytes] === "1") {
    leadingZeroBytes += 1;
  }

  const decodedLittleEndian: number[] = [];
  for (let index = leadingZeroBytes; index < value.length; index += 1) {
    const character = value[index];
    if (!character) {
      return null;
    }

    let carry = BASE58_ALPHABET.indexOf(character);
    if (carry < 0) {
      return null;
    }

    for (
      let byteIndex = 0;
      byteIndex < decodedLittleEndian.length;
      byteIndex += 1
    ) {
      carry += (decodedLittleEndian[byteIndex] ?? 0) * 58;
      decodedLittleEndian[byteIndex] = carry & 0xff;
      carry >>= 8;
    }

    while (carry > 0) {
      decodedLittleEndian.push(carry & 0xff);
      carry >>= 8;
    }
  }

  return new Uint8Array([
    ...new Array<number>(leadingZeroBytes).fill(0),
    ...decodedLittleEndian.reverse(),
  ]);
}

/** True when `value` is base58 and decodes to exactly `byteWidth` bytes. */
export function isBase58ByteWidth(value: string, byteWidth: number): boolean {
  const decoded = decodeBase58(value);
  return decoded !== null && decoded.length === byteWidth;
}

/** The decoded bytes of a base58 string as 0x-prefixed hex, or null. */
export function base58ToHex(value: string): `0x${string}` | null {
  const decoded = decodeBase58(value);
  if (decoded === null) return null;
  return `0x${[...decoded].map((byte) => byte.toString(16).padStart(2, "0")).join("")}`;
}

const WALLET_ADDRESS_NORMALIZERS: Record<
  VmId,
  (address: string | null | undefined) => AccountAddress | null
> = {
  evm: normalizeEvmAddress,
  svm: normalizeSvmAddress,
  sui: normalizeSuiAddress,
};

export function normalizeWalletAddress(
  wallet: WalletIdentity,
): WalletIdentity | null {
  const address = WALLET_ADDRESS_NORMALIZERS[wallet.vm](wallet.address);
  if (!address) {
    return null;
  }

  return {
    ...wallet,
    address,
  };
}
